import { CityModel } from './CityModel'
import { Coordinate } from './Coordinate'
import { AGENCY_STATUS } from './models'
import {
  arrayFromJSONValue,
  booleanFromJSONValue,
  coordinateFromJSONValues,
  fileURLFromJSONValue,
  stringFromJSONValue
} from './parser'

export type SocialMediaEndPoint = {
  value: string
  type: 'facebook' | 'twitter' | 'instagram' | 'youTube' | 'linkedIn' | 'flickr' | 'email' | 'website'
}

const socialMediaEndPointWithTypeAndValue = (
  type: 'facebook' | 'twitter' | 'instagram' | 'youTube' | 'linkedIn' | 'flickr' | 'email' | 'website',
  value: string
) => {
  return { type: type, value: value } as SocialMediaEndPoint
}

export class AgencyModel {
  public recordID: string
  public agencyID: string
  public name: string
  public shortName: string
  public initials: string
  public status: AGENCY_STATUS
  public detailedDescription: string
  public cities: CityModel[]
  public radioURL: string
  public coordinate: Coordinate
  public timeZone: string
  public isCPROnly: boolean
  public agencyImageURI: string
  public sponsorImageURI: string
  public darkContextAgencyImageURI: string
  public darkContextSponsorImageURI: string
  public socialMediaEndPoints: SocialMediaEndPoint[]
  public hasUnitLegend: boolean
  public hasUnitGroups: boolean
  public hasStations: boolean
  public ogr_fid: string
  public boundaryWKT: string

  public constructor(
    recordID: string,
    agencyID: string,
    name: string,
    shortName: string,
    initials: string,
    status: AGENCY_STATUS,
    detailedDescription: string,
    cities: CityModel[],
    radioURL: string,
    coordinate: Coordinate,
    timeZone: string,
    isCPROnly: boolean,
    agencyImageURI: string,
    sponsorImageURI: string,
    darkContextAgencyImageURI: string,
    darkContextSponsorImageURI: string,
    socialMediaEndPoints: SocialMediaEndPoint[],
    hasUnitLegend: boolean,
    hasUnitGroups: boolean,
    hasStations: boolean,
    ogr_fid: string,
    boundaryWKT: string
  ) {
    this.recordID = recordID
    this.agencyID = agencyID
    this.name = name
    this.shortName = shortName
    this.initials = initials
    this.status = status
    this.detailedDescription = detailedDescription
    this.cities = cities
    this.radioURL = radioURL
    this.coordinate = coordinate
    this.timeZone = timeZone
    this.isCPROnly = isCPROnly
    this.agencyImageURI = agencyImageURI
    this.sponsorImageURI = sponsorImageURI
    this.darkContextAgencyImageURI = darkContextAgencyImageURI
    this.darkContextSponsorImageURI = darkContextSponsorImageURI
    this.socialMediaEndPoints = socialMediaEndPoints
    this.hasUnitLegend = hasUnitLegend
    this.hasUnitGroups = hasUnitGroups
    this.hasStations = hasStations
    this.ogr_fid = ogr_fid
    this.boundaryWKT = boundaryWKT
  }

  public static modelFromJSON(json: any): AgencyModel {
    return new AgencyModel(
      stringFromJSONValue(json['id']),
      stringFromJSONValue(json['agencyid']),
      stringFromJSONValue(json['agencyname']).trim(),
      stringFromJSONValue(json['short_agencyname']).trim(),
      stringFromJSONValue(json['agency_initials']).trim(),
      stringFromJSONValue(json['status']),
      stringFromJSONValue(json['agency_description']),
      arrayFromJSONValue(json['cities_served']).map(value => CityModel.modelFromJSON(value)),
      stringFromJSONValue(json['livestreamurl']),
      coordinateFromJSONValues(json['agency_latitude'], json['agency_longitude']),
      stringFromJSONValue(json['timezone']),
      booleanFromJSONValue(json['cpr_only']),
      fileURLFromJSONValue(json['lightcontext_agencyimage'], 'png'),
      fileURLFromJSONValue(json['lightcontext_sponsorimage'], 'png'),
      fileURLFromJSONValue(json['darkcontext_agencyimage'], 'png'),
      fileURLFromJSONValue(json['darkcontext_sponsorimage'], 'png'),
      [
        socialMediaEndPointWithTypeAndValue('facebook', stringFromJSONValue(json['facebookname'])),
        socialMediaEndPointWithTypeAndValue('twitter', stringFromJSONValue(json['twittername'])),
        socialMediaEndPointWithTypeAndValue('instagram', stringFromJSONValue(json['instagramname'])),
        socialMediaEndPointWithTypeAndValue('youTube', stringFromJSONValue(json['youtubeURL'])),
        socialMediaEndPointWithTypeAndValue('linkedIn', stringFromJSONValue(json['linkedinname'])),
        socialMediaEndPointWithTypeAndValue('flickr', stringFromJSONValue(json['flickrusername'])),
        socialMediaEndPointWithTypeAndValue('email', stringFromJSONValue(json['public_email'])),
        socialMediaEndPointWithTypeAndValue('website', stringFromJSONValue(json['agency_website']))
      ].filter(endpoint => endpoint.value !== ''),
      booleanFromJSONValue(json['has_unit_legend']),
      booleanFromJSONValue(json['has_unit_groups']),
      booleanFromJSONValue(json['has_stations']),
      stringFromJSONValue(json['ogr_fid']),
      stringFromJSONValue(json['boundary'])
    )
  }

  public hasRadio() {
    return this.radioURL !== ''
  }

  public hasAgencyImage() {
    return this.agencyImageURI !== ''
  }

  public hasSponsorImage() {
    return this.sponsorImageURI !== ''
  }

  public boundaryCornerCoordinates(): Coordinate[] {
    const lngLatValues = /^POLYGON\(\(([0-9\.-]+ [0-9\.-]+),([0-9\.-]+ [0-9\.-]+),([0-9\.-]+ [0-9\.-]+),([0-9\.-]+ [0-9\.-]+),([0-9\.-]+ [0-9\.-]+)\)\)$/.exec(
      this.boundaryWKT
    )
    if (!lngLatValues) {
      return []
    }

    return lngLatValues.splice(1).map(lngLatValue => {
      const parts = lngLatValue.split(' ')
      return { lat: parseFloat(parts[1]), lng: parseFloat(parts[0]) }
    })
  }
}
